import { useEffect, useRef, useState } from "react";
import { Polyline, Tooltip, useMap } from "react-leaflet";
import { svg } from "leaflet";
import useMapElementStyles from "../useMapElementStyles";
import {
  findIndexById
} from "../helperFunctions/findMapElementById";
import { calcEdgeWeight } from "./calcEdgeWeight";
import "leaflet-arrowheads";
import { calculateDistance } from "../helperFunctions/findMaxDistance";
import { metricColor } from "../helperFunctions/edgeMetricColor";

// The map renders on a canvas (preferCanvas) for speed, but the animated-flow
// edges need the CSS dash animation, which only works on a real SVG <path>.
// Those edges opt into a shared SVG renderer — one per map instance, kept in a
// WeakMap so it is created lazily and never leaks across maps.
const svgRenderers = new WeakMap();
function getSvgRenderer(map) {
  let renderer = svgRenderers.get(map);
  if (!renderer) {
    renderer = svg();
    svgRenderers.set(map, renderer);
  }
  return renderer;
}

const Edge = ({
  element,
  currentMode,
  onSelect,
  selectedElement,
  mapElements,
  setMapElements,
  updateMapElements,
  weightSettings,
  flowDisplay = "none",
  arrowSize = 10,
  edgeMetric = "diameter",
  metricRange,
  index,
}) => {
  const mapStyles = useMapElementStyles();
  const map = useMap();

  const [key, setKey] = useState(0);
  const [positions, setPositions] = useState(null);
  const edgeRef = useRef(null);

  const vertexIndex = {
    start: findIndexById(mapElements, element.startNodeId),
    end: findIndexById(mapElements, element.endNodeId),
  };
  const isSaved = element.isSaved;
  const isSelected = selectedElement.id === element.id;

  useEffect(() => {
    updatePositions();
    checkLength();
  }, [
    mapElements[vertexIndex.start].position,
    mapElements[vertexIndex.end].position,
  ]);

  // Only remount when the flow-display mode changes: the animated CSS class and
  // the SVG-vs-canvas renderer are fixed at layer-creation time, so a change
  // there needs a fresh layer. Color and weight now update reactively via
  // pathOptions, so selection / metric / mode changes no longer remount the
  // layer — the main perf win with many edges.
  useEffect(() => {
    setKey((k) => k + 1);
  }, [flowDisplay]);

  // weight
  const stylesWeight = mapStyles.edge.size[currentMode]
    ? mapStyles.edge.size[currentMode]
    : mapStyles.edge.size.default;

  // in the result view the edge thickness represents the selected metric
  // (diameter or first/final year pipe flow); elsewhere it uses the style size
  const metricProp =
    (element.calc && element.calc.find((obj) => obj.label === edgeMetric)) ||
    null;

  const weight =
    currentMode === "view" && metricProp && metricProp.value != null
      ? calcEdgeWeight(metricProp.value, weightSettings) || stylesWeight
      : stylesWeight;

  const pressurePipeProp = element.props?.find(
      (prop) => prop.key === "pressure_pipe"
    );
    const isPressurePipe = pressurePipeProp?.value; // This will be true or false

    // result view: color the edge by the selected metric value (green = low →
    // red = high), so intensity and thickness together encode the value
    const gradientColor =
      currentMode === "view" && metricProp && metricProp.value != null
        ? metricColor(Number(metricProp.value), metricRange, edgeMetric)
        : null;

    // Set color based on priority
    var color;
    if (isSelected) {
      color = mapStyles.edge.colors.selected; // selection stays visible
    } else if (gradientColor) {
      color = gradientColor; // result view: value gradient
    } else if (isPressurePipe) {
      color = "red"; // pressure pipe indicator (design view)
    } else if (!isSaved) {
      color = mapStyles.node.colors.notSaved; // not saved color
    } else {
      color = mapStyles.edge.colors.default; // Default color
    }

  function updatePositions() {
    const startNode = mapElements[vertexIndex.start];
    const endNode = mapElements[vertexIndex.end];
    setPositions([startNode.position, endNode.position]);
  }

  function checkLength() {
    const startNode = mapElements[vertexIndex.start];
    const endNode = mapElements[vertexIndex.end];
    // round the computed pipe length to 2 decimals (cm) instead of storing the
    // raw float with many decimals
    const newLength =
      Math.round(
        calculateDistance(startNode.position, endNode.position) * 1000 * 100
      ) / 100;
    const lengthProp = element.props?.find((obj) => obj.label === "length");

    if (
      !lengthProp ||
      Math.round(lengthProp.value * 100) !== Math.round(newLength * 100)
    )
      updateLength(newLength);
  }

  function updateLength(newLength) {
    const newProps = [
      {
        value: newLength,
        key: "length",
        label: "length",
        type: "display",
        unit: "m",
      },
    ];
    element.props?.forEach((prop) => {
      if (prop.label !== "length") return newProps.push(prop);
    });
    // The length is derived from the node positions, so this is not a user
    // edit: update immutably, keep `isSaved` and bypass the undo history
    // (falls back to setMapElements when the raw setter is not provided).
    const applyUpdate = updateMapElements || setMapElements;
    applyUpdate((prev) =>
      (prev || mapElements).map((item) =>
        item.id === element.id ? { ...item, props: newProps } : item
      )
    );
  }

  const handleMouseOver = () => {
    if (edgeRef.current) {
      edgeRef.current.setStyle({ color: mapStyles.edge.colors.hover }); // Change color on hover
    }
  };

  const handleMouseOut = () => {
    if (edgeRef.current) {
      edgeRef.current.setStyle({ color: color }); // Reset color on mouseout
    }
  };

  const eventHandlers = {
    click: () => onSelect(element),
    mouseover: handleMouseOver,
    mouseout: handleMouseOut,
  };

  // Flow direction is visualized in one of three modes, cycled via the map
  // utility button (flowDisplay): "arrows" (one static arrowhead per edge),
  // "animated" (flowing dash animation) or "none" (plain edges).
  //
  // Show a single arrowhead per edge pointing in flow direction (start -> end).
  // fill:false makes leaflet-arrowheads draw an OPEN arrow (just the two legs,
  // no closed/filled triangle).
  //
  // Pressure pipes always show their arrow, regardless of the mode, and use a
  // FILLED (closed) arrowhead so the pumping direction stands out from the open
  // arrows on gravity edges.
  const showArrow = flowDisplay === "arrows" || isPressurePipe;
  useEffect(() => {
    const polyline = edgeRef.current;
    if (!polyline || !polyline.arrowheads) return;
    if (showArrow) {
      // Gravity edges show their single direction arrow at the MIDPOINT: offset
      // the (invisible) ghost polyline's end back by half the edge length so the
      // "endonly" arrow lands in the middle. Pressure-pipe arrows stay at the
      // end so the pumping target reads clearly.
      const halfLengthMeters =
        (calculateDistance(positions[0], positions[1]) * 1000) / 2;
      polyline.arrowheads({
        fill: isPressurePipe, // pressure pipes: closed/filled head; others: open
        color: isPressurePipe ? color : mapStyles.edge.colors.arrow || color,
        weight: 2, // thin legs regardless of the (diameter-based) edge weight
        yawn: 45,
        // size is user-adjustable; pressure pipes render a bit larger so they
        // keep standing out from the open gravity-edge arrows
        size: `${isPressurePipe ? arrowSize + 4 : arrowSize}px`,
        frequency: "endonly", // exactly one arrow per edge
        offsets: isPressurePipe ? undefined : { end: `${halfLengthMeters}m` },
      });
      polyline._update && polyline._update();
    } else if (polyline.deleteArrowheads) {
      polyline.deleteArrowheads();
    }
    return () => {
      if (polyline && polyline.deleteArrowheads) polyline.deleteArrowheads();
    };
    // `key` is included on purpose: a flowDisplay change remounts the Polyline
    // (fresh layer / renderer), so the arrowheads must be re-applied to the new
    // layer even though the other deps are unchanged across the remount.
  }, [showArrow, positions, weight, color, key, isPressurePipe, arrowSize]);

  if (!positions) return;
  return (
    <Polyline
      key={key}
      className={flowDisplay === "animated" ? "edge-animated" : undefined}
      // animated edges need the CSS dash animation, which only works on an SVG
      // path -> force the SVG renderer for them; all other edges use the map's
      // default canvas renderer (preferCanvas) for speed
      renderer={flowDisplay === "animated" ? getSvgRenderer(map) : undefined}
      ref={edgeRef}
      // color/weight via pathOptions so react-leaflet restyles the layer in
      // place instead of remounting on every selection / metric change
      pathOptions={{ color, weight }}
      eventHandlers={eventHandlers}
      positions={positions}
    >
      {element.name && (
        <Tooltip direction="bottom" offset={[0, weight / 2]} opacity={1}>
          {element.name}
        </Tooltip>
      )}
    </Polyline>
  );
};

export default Edge;
